import React, { useCallback, useState } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import FontAwesome from '@expo/vector-icons/FontAwesome';

import { Colors } from '@/constants/theme';
import { CALL_QUALITY_ISSUES, MAX_CALL_RATING, shouldAskForIssue } from '@/services/calls/callFeedback';
import type { CallFeedback, CallQualityIssue } from '@/services/calls/types';

/**
 * Post-call quality survey, shared by the native and web call screens: a
 * 1–5 star rating and, below a perfect score, a single-choice list of common
 * problems. Designed for a white surface — the parent supplies the card /
 * window (native wraps it in [CallFeedbackView], web in its call card).
 *
 * Sending is the parent's job (`onSubmit` is awaited and may throw): a failure
 * is shown inline and the guide can try again or skip, so a broken network at
 * the end of a call never traps them on this screen.
 */

interface CallFeedbackFormProps {
    onSubmit: (feedback: CallFeedback) => Promise<void>;
    onSkip: () => void;
}

const STARS = Array.from({ length: MAX_CALL_RATING }, (_, i) => i + 1);

export function CallFeedbackForm({ onSubmit, onSkip }: CallFeedbackFormProps) {
    const [rating, setRating] = useState(0);
    const [issue, setIssue] = useState<CallQualityIssue | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const askForIssue = shouldAskForIssue(rating);

    const pickRating = useCallback((value: number) => {
        setRating(value);
        setError(null);
        // A perfect score has nothing to explain — clear a previously picked
        // problem so it isn't sent along by surprise.
        if (!shouldAskForIssue(value)) setIssue(null);
    }, []);

    const submit = useCallback(async () => {
        if (!rating || submitting) return;
        setSubmitting(true);
        setError(null);
        try {
            await onSubmit({ rating, issue: askForIssue ? issue : null });
        } catch (e) {
            setError((e as Error)?.message || 'Your feedback could not be sent. Please try again.');
            setSubmitting(false);
        }
    }, [rating, issue, askForIssue, submitting, onSubmit]);

    const canSend = rating > 0 && !submitting;

    return (
        <View style={styles.form}>
            <Text style={styles.title}>How did we do?</Text>
            <Text style={styles.subtitle}>Please rate the call to help us improve calling.</Text>

            <View style={styles.stars} accessibilityRole="radiogroup">
                {STARS.map((value) => {
                    const filled = value <= rating;
                    return (
                        <Pressable
                            key={value}
                            style={styles.star}
                            onPress={() => pickRating(value)}
                            disabled={submitting}
                            hitSlop={6}
                            accessibilityRole="radio"
                            accessibilityState={{ selected: value === rating }}
                            accessibilityLabel={`${value} of ${MAX_CALL_RATING} stars`}
                        >
                            <FontAwesome name={filled ? 'star' : 'star-o'} size={40} color={filled ? STAR_FILLED : STAR_EMPTY} />
                            <Text style={[styles.starLabel, filled && styles.starLabelFilled]}>{value}</Text>
                        </Pressable>
                    );
                })}
            </View>

            {askForIssue && (
                <View style={styles.issues} accessibilityRole="radiogroup">
                    <Text style={styles.issuesTitle}>What went wrong?</Text>
                    {CALL_QUALITY_ISSUES.map((option) => {
                        const selected = option.value === issue;
                        return (
                            <Pressable
                                key={option.value}
                                style={styles.issueRow}
                                onPress={() => setIssue(selected ? null : option.value)}
                                disabled={submitting}
                                accessibilityRole="radio"
                                accessibilityState={{ selected }}
                            >
                                <View style={[styles.radio, selected && styles.radioSelected]}>
                                    {selected && <View style={styles.radioDot} />}
                                </View>
                                <Text style={[styles.issueLabel, selected && styles.issueLabelSelected]}>{option.label}</Text>
                            </Pressable>
                        );
                    })}
                </View>
            )}

            {error ? <Text style={styles.error}>{error}</Text> : null}

            <TouchableOpacity
                style={[styles.sendButton, !canSend && styles.sendButtonDisabled]}
                activeOpacity={0.8}
                onPress={submit}
                disabled={!canSend}
                accessibilityRole="button"
                accessibilityState={{ disabled: !canSend }}
            >
                {submitting ? (
                    <ActivityIndicator size="small" color="#FFF" />
                ) : (
                    <Text style={styles.sendText}>Send Feedback</Text>
                )}
            </TouchableOpacity>

            <TouchableOpacity
                style={styles.skipButton}
                activeOpacity={0.7}
                onPress={onSkip}
                disabled={submitting}
                accessibilityRole="button"
            >
                <Text style={styles.skipText}>Skip</Text>
            </TouchableOpacity>
        </View>
    );
}

const STAR_FILLED = '#F5B301';
const STAR_EMPTY = '#C9CFD6';

const styles = StyleSheet.create({
    form: {
        alignSelf: 'stretch',
        alignItems: 'center',
    },
    title: {
        fontSize: 20,
        fontWeight: '700',
        color: '#1A1A1A',
        textAlign: 'center',
    },
    subtitle: {
        fontSize: 15,
        color: '#8A9099',
        textAlign: 'center',
        marginTop: 4,
        lineHeight: 20,
    },
    stars: {
        flexDirection: 'row',
        justifyContent: 'center',
        gap: 8,
        marginTop: 20,
    },
    star: {
        alignItems: 'center',
        paddingHorizontal: 4,
        paddingVertical: 4,
    },
    starLabel: {
        marginTop: 4,
        fontSize: 12,
        color: '#8A9099',
    },
    starLabelFilled: {
        color: '#1A1A1A',
    },
    issues: {
        alignSelf: 'stretch',
        marginTop: 20,
        gap: 2,
    },
    issuesTitle: {
        fontSize: 13,
        fontWeight: '600',
        color: '#8A9099',
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        marginBottom: 6,
    },
    issueRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        paddingVertical: 8,
    },
    radio: {
        width: 22,
        height: 22,
        borderRadius: 11,
        borderWidth: 2,
        borderColor: '#C4CAD2',
        alignItems: 'center',
        justifyContent: 'center',
    },
    radioSelected: {
        borderColor: Colors.brand,
    },
    radioDot: {
        width: 11,
        height: 11,
        borderRadius: 6,
        backgroundColor: Colors.brand,
    },
    issueLabel: {
        flex: 1,
        fontSize: 15,
        color: '#343A40',
    },
    issueLabelSelected: {
        color: '#1A1A1A',
        fontWeight: '600',
    },
    error: {
        marginTop: 16,
        fontSize: 14,
        color: '#E53935',
        textAlign: 'center',
        lineHeight: 19,
    },
    sendButton: {
        alignSelf: 'stretch',
        height: 50,
        borderRadius: 25,
        backgroundColor: Colors.brand,
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 24,
    },
    sendButtonDisabled: {
        opacity: 0.45,
    },
    sendText: {
        color: '#FFF',
        fontSize: 16,
        fontWeight: '600',
    },
    skipButton: {
        marginTop: 12,
        paddingVertical: 8,
        paddingHorizontal: 16,
    },
    skipText: {
        fontSize: 15,
        color: '#8A9099',
        fontWeight: '500',
    },
});
